/**
 * Tareas y actividades del CRM de Business — `OD-0050` D-07 sobre `RC-0017`.
 *
 * Tres piezas:
 *
 *   PantallaTareas      la entrada "Tareas" del menú: lo pendiente agrupado en
 *                       vencidas / hoy / próximas / sin fecha, y lo hecho
 *   BloqueActividades   lo mismo, dentro de la ficha de un cliente, una
 *                       oportunidad, un presupuesto o una factura
 *                       (`OD-0050-ENMIENDA-01`), con su historial completo
 *   FormularioActividad el alta y la edición, de una sola pantalla
 *
 * Una tarea y una llamada son lo mismo con distinto tipo (`D-01`). Los grupos
 * los calcula ESTA pantalla con el día LOCAL: el servidor no sabe en qué zona
 * horaria está el usuario y solo ordena por fecha.
 *
 * Fuera de Business la API contesta 402 y se explica que es de Business
 * (`D-04`), como en Oportunidades. Nada aquí avisa por correo (`D-05`).
 *
 * `OD-0062` (ACCEPTED 2026-10-25) añade dos cosas al formulario: «Se repite»
 * (E-09, la siguiente la crea la API al marcarla como hecha) y, con un cliente
 * elegido, enlazarla a uno de sus presupuestos o facturas (E-10).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  api,
  REPETICIONES_ACTIVIDAD,
  TIPOS_ACTIVIDAD,
  type Actividad,
  type Cliente,
  type DatosActividad,
  type FiltroActividades,
  type RepeticionActividad,
  type TipoActividad,
} from "../api/cliente";
import { t } from "../i18n/es";
import { Icono } from "../ui/Iconos";
import { AreaTexto, Aviso, Boton, Buscador, Campo, Casilla, Selector, Tarjeta } from "../ui/primitivas";
import { diaLegible } from "./PantallaOportunidades";

type Grupo = keyof typeof t.tareas.grupos;
const GRUPOS: readonly Grupo[] = ["vencidas", "hoy", "proximas", "sinFecha"];

/** El día de hoy en la zona del USUARIO, como AAAA-MM-DD. */
function hoyLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function grupoDe(a: Actividad, hoy: string): Grupo {
  if (a.dueDate === null) return "sinFecha";
  if (a.dueDate < hoy) return "vencidas";
  return a.dueDate === hoy ? "hoy" : "proximas";
}

/** Dentro de `dias` días, en el día LOCAL: la fecha que propone un seguimiento. */
function enDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Cómo se abre desde una tarea el presupuesto o la factura a la que va
 * (`OD-0050-ENMIENDA-01` E-04). Sin quien lo abra, el enlace es solo texto. Y en
 * la ficha de ese mismo documento no se repite.
 */
type Enlaces = {
  readonly abrirPresupuesto?: (id: string) => void;
  readonly abrirFactura?: (id: string) => void;
  readonly ficha?: FiltroActividades;
};
const EnlacesContext = createContext<Enlaces>({});

/** Lo que un botón de seguimiento deja escrito en el formulario. Se puede cambiar. */
export type Plantilla = { readonly kind: TipoActividad; readonly title: string; readonly dueDate: string | null };

/**
 * Un presupuesto o una factura que se puede enlazar desde el formulario
 * (`OD-0062` E-10). `valor` lleva delante de qué es: `q:` o `f:`.
 */
type DocumentoElegible = { readonly valor: string; readonly clienteId: string; readonly texto: string; readonly fecha: string };

/** Los del cliente que no están cancelados ni anulados, lo más reciente primero. */
function documentosDelCliente(documentos: readonly DocumentoElegible[], clienteId: string): readonly DocumentoElegible[] {
  // AAAA-MM-DD se ordena igual como texto.
  return documentos.filter((d) => d.clienteId === clienteId).sort((a, b) => b.fecha.localeCompare(a.fecha));
}

async function cargarDocumentos(): Promise<readonly DocumentoElegible[]> {
  // Un rol sin permiso para ver facturas (403) sigue pudiendo enlazar presupuestos.
  const [presupuestos, facturas] = await Promise.all([api.listarPresupuestos(null), api.listarFacturas(null)]);
  return [
    ...(presupuestos.ok ? presupuestos.datos : [])
      .filter((q) => q.status !== "CANCELLED")
      .map((q) => ({ valor: `q:${q.id}`, clienteId: q.customerId, texto: t.tareas.dePresupuesto(q.number), fecha: q.issueDate })),
    ...(facturas.ok ? facturas.datos : [])
      .filter((f) => f.status !== "VOIDED" && f.status !== "DISCARDED")
      .map((f) => ({ valor: `f:${f.id}`, clienteId: f.customerId, texto: t.tareas.deFactura(f.number), fecha: f.issueDate ?? f.updatedAt.slice(0, 10) })),
  ];
}

/** Un instante ISO -> su día LOCAL legible. */
function diaLocalDe(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

function SoloBusiness({ onVerPlan }: { onVerPlan: () => void }) {
  return (
    <div className="estado-vacio">
      <span className="estado-vacio-icono" aria-hidden="true">
        <Icono nombre="tareas" />
      </span>
      <p className="estado-vacio-titulo">{t.tareas.soloBusinessTitulo}</p>
      <p>{t.tareas.soloBusiness}</p>
      <Boton onClick={onVerPlan}>{t.tareas.verPlanes}</Boton>
    </div>
  );
}

// ------------------------------------------------------------ formulario

export function FormularioActividad({
  inicial = null,
  clienteFijo = null,
  oportunidadFija = null,
  presupuestoFijo = null,
  facturaFija = null,
  plantilla = null,
  apuntar = false,
  onGuardada,
  onCancelar,
  onSesionCaducada,
}: {
  /** Para editar. `null` es un alta. */
  inicial?: Actividad | null;
  /** Desde una ficha: el cliente y la oportunidad ya están puestos y no se eligen. */
  clienteFijo?: string | null;
  oportunidadFija?: string | null;
  /** Desde la ficha de un presupuesto o de una factura (E-01). */
  presupuestoFijo?: string | null;
  facturaFija?: string | null;
  /** "Apuntar seguimiento": tipo, título y fecha ya escritos (E-04). */
  plantilla?: Plantilla | null;
  /** "Apuntar lo que ya has hecho": se guarda como hecha (`D-02`). */
  apuntar?: boolean;
  onGuardada: (a: Actividad) => void;
  onCancelar: () => void;
  onSesionCaducada: () => void;
}) {
  const fija = clienteFijo !== null || oportunidadFija !== null || presupuestoFijo !== null || facturaFija !== null;
  const [kind, setKind] = useState<TipoActividad>(inicial?.kind ?? plantilla?.kind ?? (apuntar ? "CALL" : "TASK"));
  const [titulo, setTitulo] = useState(inicial?.title ?? plantilla?.title ?? "");
  const [fecha, setFecha] = useState(inicial?.dueDate ?? plantilla?.dueDate ?? "");
  const [hora, setHora] = useState(inicial?.dueTime ?? "");
  const [importante, setImportante] = useState(inicial?.important ?? false);
  const [notas, setNotas] = useState(inicial?.notes ?? "");
  const [clientes, setClientes] = useState<readonly Cliente[]>([]);
  const [clienteId, setClienteId] = useState<string | null>(inicial?.customer?.id ?? null);
  const [clienteTexto, setClienteTexto] = useState(inicial?.customer?.displayName ?? "");
  const [repetir, setRepetir] = useState<RepeticionActividad | "">(inicial?.repeat ?? "");
  // E-10: el presupuesto o la factura, elegidos aquí. Sin tocarlo, se conserva lo que tenía.
  const documentoInicial = inicial?.quote != null ? `q:${inicial.quote.id}` : inicial?.invoice != null ? `f:${inicial.invoice.id}` : "";
  const [documento, setDocumento] = useState(documentoInicial);
  const [documentos, setDocumentos] = useState<readonly DocumentoElegible[] | null>(null);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Solo se cargan los clientes si hay que elegir uno: desde una ficha no.
  useEffect(() => {
    if (fija) return;
    void api.listarClientes("ACTIVE").then((r) => {
      if (r.ok) setClientes(r.datos);
    });
  }, [fija]);
  const opciones = useMemo(() => clientes.map((c) => ({ id: c.id, texto: c.displayName, claves: [c.displayName, c.taxIdNormalized] })), [clientes]);
  // Sus presupuestos y facturas, la primera vez que se elige un cliente.
  const hayCliente = !fija && clienteId !== null;
  useEffect(() => {
    if (!hayCliente || documentos !== null) return;
    void cargarDocumentos().then(setDocumentos);
  }, [hayCliente, documentos]);
  const delCliente = hayCliente && documentos !== null && clienteId !== null ? documentosDelCliente(documentos, clienteId) : [];
  // Lo que ya tenía se ofrece aunque la lista no lo traiga (p. ej. una factura ya anulada).
  const opcionesDocumento = [
    { valor: "", texto: t.tareas.sinDocumento },
    ...(documentoInicial !== "" && !delCliente.some((d) => d.valor === documentoInicial)
      ? [{ valor: documentoInicial, texto: inicial?.quote != null ? t.tareas.dePresupuesto(inicial.quote.number) : t.tareas.deFactura(inicial?.invoice?.number ?? null) }]
      : []),
    ...delCliente.map((d) => ({ valor: d.valor, texto: d.texto })),
  ];
  // Lo escrito que no es ningún cliente NO se guarda en silencio como «sin
  // cliente» (prueba del propietario en staging, 2026-10-25).
  const clienteSinElegir = !fija && clienteTexto.trim() !== "" && clienteId === null;
  // Una tarea pendiente con la fecha pasada se puede guardar, pero se avisa.
  const fechaPasada = !apuntar && inicial?.status !== "DONE" && fecha !== "" && fecha < hoyLocal();

  async function guardar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setGeneral(null);
    if (clienteSinElegir) {
      setErrores({ customerId: t.tareas.clienteNoElegido });
      return;
    }
    setGuardando(true);
    const elegido = documento === documentoInicial ? null : documento;
    const datos: DatosActividad = {
      kind,
      title: titulo,
      notes: notas === "" ? null : notas,
      dueDate: fecha === "" ? null : fecha,
      dueTime: hora === "" ? null : hora,
      important: importante,
      // Editando, se conserva lo que ya tenía; desde una ficha, lo de la ficha.
      customerId: clienteFijo ?? (fija ? null : clienteId),
      opportunityId: oportunidadFija ?? inicial?.opportunity?.id ?? null,
      quoteId: presupuestoFijo ?? (elegido === null ? (inicial?.quote?.id ?? null) : elegido.startsWith("q:") ? elegido.slice(2) : null),
      invoiceId: facturaFija ?? (elegido === null ? (inicial?.invoice?.id ?? null) : elegido.startsWith("f:") ? elegido.slice(2) : null),
      // E-09: solo con fecha, y no al apuntar algo ya hecho.
      repeat: apuntar || fecha === "" || repetir === "" ? null : repetir,
    };
    const r = inicial === null ? await api.crearActividad(datos, apuntar) : await api.editarActividad(inicial.id, datos);
    setGuardando(false);
    if (r.ok) {
      onGuardada(r.datos);
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setGeneral(t.tareas.soloLectura);
    const campos = r.error?.campos;
    if (r.estado === 422 && campos !== undefined && Object.keys(campos).length > 0) {
      setErrores(Object.fromEntries(Object.entries(campos).map(([k, v]) => [k, t.tareas.errores[v] ?? t.tareas.errorGuardar])));
      return;
    }
    setGeneral(t.tareas.errorGuardar);
  }

  return (
    <form className="formulario-acotado actividad-formulario" onSubmit={(e) => void guardar(e)} noValidate>
      {apuntar ? <p className="ayuda-neutra">{t.tareas.apuntarAyuda}</p> : null}
      <div className="fila-campos">
        <Selector
          id="act-tipo"
          etiqueta={t.tareas.tipo}
          valor={kind}
          opciones={TIPOS_ACTIVIDAD.map((k) => ({ valor: k, texto: t.tareas.tipos[k] }))}
          onChange={setKind}
          error={errores.kind ?? null}
        />
        <Campo id="act-fecha" etiqueta={t.tareas.fecha} tipo="date" valor={fecha} onChange={setFecha} requerido={false} error={errores.dueDate ?? null} />
        <Campo id="act-hora" etiqueta={t.tareas.hora} tipo="time" valor={hora} onChange={setHora} requerido={false} error={errores.dueTime ?? null} />
      </div>
      {fechaPasada && errores.dueDate === undefined ? <Aviso tono="aviso">{t.tareas.fechaPasada}</Aviso> : null}
      {!apuntar && fecha !== "" ? (
        <>
          <Selector
            id="act-repetir"
            etiqueta={t.tareas.repetir}
            valor={repetir}
            opciones={[{ valor: "" as const, texto: t.tareas.noSeRepite }, ...REPETICIONES_ACTIVIDAD.map((r) => ({ valor: r, texto: t.tareas.repeticiones[r] }))]}
            onChange={setRepetir}
            error={errores.repeat ?? null}
          />
          {repetir !== "" ? <p className="ayuda-neutra">{t.tareas.repetirAyuda}</p> : null}
        </>
      ) : null}
      <Campo
        id="act-titulo"
        etiqueta={apuntar ? t.tareas.quePaso : t.tareas.queHay}
        ayuda={apuntar ? t.tareas.quePasoAyuda : t.tareas.queHayAyuda}
        valor={titulo}
        onChange={setTitulo}
        error={errores.title ?? null}
      />
      {!fija ? (
        <Buscador
          id="act-cliente"
          etiqueta={t.tareas.cliente}
          marcador={t.tareas.eligeCliente}
          texto={clienteTexto}
          opciones={opciones}
          onTexto={(v) => {
            setClienteTexto(v);
            if (errores.customerId !== undefined) setErrores((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== "customerId")));
          }}
          onElegir={(id) => {
            if (id !== clienteId) setDocumento("");
            setClienteId(id);
          }}
          error={errores.customerId ?? null}
        />
      ) : null}
      {hayCliente ? (
        <Selector
          id="act-documento"
          etiqueta={t.tareas.documento}
          valor={documento}
          opciones={opcionesDocumento}
          onChange={setDocumento}
          deshabilitado={documentos === null}
        />
      ) : null}
      <AreaTexto id="act-notas" etiqueta={t.tareas.notas} valor={notas} onChange={setNotas} error={errores.notes ?? null} filas={2} />
      {!apuntar ? <Casilla id="act-importante" etiqueta={t.tareas.importante} marcada={importante} onChange={setImportante} /> : null}
      {(["opportunityId", "quoteId", "invoiceId"] as const).map((k) =>
        errores[k] !== undefined ? (
          <Aviso key={k} tono="error">
            {errores[k]}
          </Aviso>
        ) : null,
      )}
      {general !== null ? <Aviso tono="error">{general}</Aviso> : null}
      <div className="fila-botones">
        <Boton tipo="submit" deshabilitado={guardando}>
          {guardando ? t.tareas.guardando : t.tareas.guardar}
        </Boton>
        <Boton variante="terciario" onClick={onCancelar}>
          {t.tareas.cancelar}
        </Boton>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ una fila

function FilaActividad({
  a,
  enFicha,
  onCambio,
  onSesionCaducada,
}: {
  a: Actividad;
  /** En una ficha no se repite el cliente. */
  enFicha: boolean;
  /**
   * `recienHecha`: la que se acaba de marcar como hecha, para ofrecer deshacerlo;
   * `siguiente`, la que ha nacido al hacerla si se repite (E-09).
   */
  onCambio: (recienHecha?: Actividad, siguiente?: Actividad | null) => void;
  onSesionCaducada: () => void;
}) {
  const [modo, setModo] = useState<"ver" | "editar" | "borrar">("ver");
  const [aviso, setAviso] = useState<string | null>(null);
  const enlaces = useContext(EnlacesContext);
  const hecha = a.status === "DONE";

  async function tratar(r: { readonly ok: boolean; readonly estado?: number }): Promise<void> {
    if (r.ok) return onCambio();
    if (r.estado === 401) return onSesionCaducada();
    setAviso(r.estado === 403 ? t.tareas.soloLectura : t.tareas.errorGuardar);
  }

  if (modo === "editar") {
    return (
      <li className="actividad actividad-editando">
        <FormularioActividad
          inicial={a}
          clienteFijo={enFicha ? (a.customer?.id ?? null) : null}
          oportunidadFija={enFicha ? (a.opportunity?.id ?? null) : null}
          presupuestoFijo={enFicha ? (a.quote?.id ?? null) : null}
          facturaFija={enFicha ? (a.invoice?.id ?? null) : null}
          onGuardada={() => {
            setModo("ver");
            onCambio();
          }}
          onCancelar={() => setModo("ver")}
          onSesionCaducada={onSesionCaducada}
        />
      </li>
    );
  }

  // En "Hoy" la fecha sobra: el grupo ya la dice. Queda la hora, si la hay.
  const fecha = a.dueDate === null || a.dueDate === hoyLocal() ? null : diaLegible(a.dueDate);
  const cuando = [fecha, a.dueTime].filter((x): x is string => x !== null).join(" · ");
  return (
    <li className={`actividad${hecha ? " actividad-hecha" : ""}${a.important && !hecha ? " actividad-importante" : ""}`}>
      <button
        type="button"
        className="actividad-marca"
        aria-pressed={hecha}
        aria-label={hecha ? t.tareas.deshacer(a.title) : t.tareas.marcarHecha(a.title)}
        onClick={() => void api.marcarActividad(a.id, !hecha).then((r) => (r.ok && !hecha ? onCambio(a, r.datos.siguiente) : tratar(r)))}
      >
        {hecha ? <Icono nombre="tareas" /> : null}
      </button>
      <div className="actividad-texto">
        <span className="actividad-titulo">{a.title}</span>
        <span className="ayuda-neutra">
          {t.tareas.tipos[a.kind]}
          {a.important && !hecha ? ` · ${t.tareas.importante}` : ""}
          {cuando !== "" && !hecha ? ` · ${cuando}` : ""}
          {a.repeat !== null && !hecha ? ` · ${t.tareas.repeticiones[a.repeat]}` : ""}
          {hecha && a.doneAt !== null ? ` · ${t.tareas.hechaEl(diaLocalDe(a.doneAt))}` : ""}
          {!enFicha && a.customer !== null ? ` · ${a.customer.displayName}` : ""}
          {a.opportunity !== null ? ` · ${t.tareas.deOportunidad(a.opportunity.title)}` : ""}
        </span>
        <EnlaceDocumento
          doc={a.quote}
          texto={a.quote === null ? "" : t.tareas.dePresupuesto(a.quote.number)}
          abrir={enlaces.abrirPresupuesto}
          oculto={a.quote !== null && enlaces.ficha?.presupuestoId === a.quote.id}
        />
        <EnlaceDocumento
          doc={a.invoice}
          texto={a.invoice === null ? "" : t.tareas.deFactura(a.invoice.number)}
          abrir={enlaces.abrirFactura}
          oculto={a.invoice !== null && enlaces.ficha?.facturaId === a.invoice.id}
        />
        {a.notes !== null ? <span className="actividad-notas">{a.notes}</span> : null}
        {aviso !== null ? <Aviso tono="error">{aviso}</Aviso> : null}
        {modo === "borrar" ? (
          <div className="fila-botones">
            <span>{t.tareas.confirmarEliminar}</span>
            <Boton variante="peligro" onClick={() => void api.eliminarActividad(a.id).then(tratar)}>
              {t.tareas.siEliminar}
            </Boton>
            <Boton variante="terciario" onClick={() => setModo("ver")}>
              {t.tareas.cancelar}
            </Boton>
          </div>
        ) : (
          <div className="fila-botones actividad-acciones">
            <Boton variante="terciario" onClick={() => setModo("editar")}>
              {t.tareas.editar}
            </Boton>
            <Boton variante="terciario" onClick={() => setModo("borrar")}>
              {t.tareas.eliminar}
            </Boton>
          </div>
        )}
      </div>
    </li>
  );
}

/** El presupuesto o la factura de una tarea: un enlace si hay quien lo abra, texto si no. */
function EnlaceDocumento({
  doc,
  texto,
  abrir,
  oculto,
}: {
  doc: { readonly id: string } | null;
  texto: string;
  abrir: ((id: string) => void) | undefined;
  oculto: boolean;
}) {
  if (doc === null || oculto) return null;
  if (abrir === undefined) return <span className="actividad-documento ayuda-neutra">{texto}</span>;
  return (
    <button type="button" className="actividad-documento enlace-en-texto" onClick={() => abrir(doc.id)}>
      {texto}
    </button>
  );
}

function Agrupadas({ lista, enFicha, onCambio, onSesionCaducada }: {
  lista: readonly Actividad[];
  enFicha: boolean;
  onCambio: (recienHecha?: Actividad, siguiente?: Actividad | null) => void;
  onSesionCaducada: () => void;
}) {
  const hoy = hoyLocal();
  return (
    <>
      {GRUPOS.map((g) => {
        const del = lista.filter((a) => grupoDe(a, hoy) === g);
        if (del.length === 0) return null;
        return (
          <section key={g} className={`actividades-grupo actividades-grupo-${g}`} aria-label={t.tareas.grupos[g]}>
            <h3 className="actividades-grupo-titulo">
              {t.tareas.grupos[g]} <span className="tablero-cuenta">{del.length}</span>
            </h3>
            <ul className="actividades">
              {del.map((a) => (
                <FilaActividad key={a.id} a={a} enFicha={enFicha} onCambio={onCambio} onSesionCaducada={onSesionCaducada} />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}

// ------------------------------------------------------------ la pantalla

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "solo-business" }
  | { readonly tipo: "datos"; readonly lista: readonly Actividad[]; readonly pendientes: number; readonly cortada: boolean };

function usarActividades(vista: "PENDING" | "DONE", filtro: FiltroActividades, onSesionCaducada: () => void) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const { clienteId = null, oportunidadId = null, presupuestoId = null, facturaId = null } = filtro;
  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.listarActividades(vista, { clienteId, oportunidadId, presupuestoId, facturaId });
    if (r.ok) return setEstado({ tipo: "datos", lista: r.datos.actividades, pendientes: r.datos.pendientes, cortada: r.datos.cortada });
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 402) return setEstado({ tipo: "solo-business" });
    setEstado({ tipo: r.estado === 403 ? "sin-permiso" : "error" });
  }, [vista, clienteId, oportunidadId, presupuestoId, facturaId, onSesionCaducada]);
  useEffect(() => {
    void cargar();
  }, [cargar]);
  return { estado, cargar };
}

export function PantallaTareas({
  onVerPlan,
  onAbrirPresupuesto,
  onAbrirFactura,
  onSesionCaducada,
}: {
  onVerPlan: () => void;
  onAbrirPresupuesto?: (id: string) => void;
  onAbrirFactura?: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  const [vista, setVista] = useState<"PENDING" | "DONE">("PENDING");
  const [alta, setAlta] = useState<"nada" | "tarea" | "apuntar">("nada");
  const { estado, cargar } = usarActividades(vista, {}, onSesionCaducada);
  // Al marcarla, la tarea se va a «Hechas» y desaparece de aquí: se ofrece
  // deshacerlo en el sitio (D-02 ya tiene «Deshacer», pero estaba en la otra
  // pestaña; prueba del propietario en staging, 2026-10-25).
  const [recienHecha, setRecienHecha] = useState<Actividad | null>(null);
  // E-09: la que ha nacido al hacer una que se repite, para decir cuándo toca.
  const [siguiente, setSiguiente] = useState<Actividad | null>(null);
  const alCambiar = (hecha?: Actividad, nueva?: Actividad | null): void => {
    setRecienHecha(hecha ?? null);
    setSiguiente(nueva ?? null);
    void cargar();
  };
  async function deshacer(a: Actividad): Promise<void> {
    const r = await api.marcarActividad(a.id, false);
    if (!r.ok && r.estado === 401) return onSesionCaducada();
    setRecienHecha(null);
    setSiguiente(null);
    void cargar();
  }
  const enlaces = useMemo(() => ({ abrirPresupuesto: onAbrirPresupuesto, abrirFactura: onAbrirFactura }), [onAbrirPresupuesto, onAbrirFactura]);

  if (estado.tipo === "solo-business") {
    return (
      <Tarjeta titulo={t.tareas.titulo}>
        <SoloBusiness onVerPlan={onVerPlan} />
      </Tarjeta>
    );
  }

  const pendientes = estado.tipo === "datos" ? estado.pendientes : 0;
  return (
    <EnlacesContext.Provider value={enlaces}>
    <Tarjeta titulo={t.tareas.titulo}>
      <div className="barra-acciones">
        <div className="pestanas" role="group" aria-label={t.tareas.vistas}>
          <button type="button" className="pestana" aria-pressed={vista === "PENDING"} onClick={() => setVista("PENDING")}>
            {t.tareas.pendientes(pendientes)}
          </button>
          <button type="button" className="pestana" aria-pressed={vista === "DONE"} onClick={() => {
            setRecienHecha(null);
            setVista("DONE");
          }}>
            {t.tareas.hechas}
          </button>
        </div>
        <div className="fila-botones">
          <Boton variante="secundario" onClick={() => setAlta("apuntar")} deshabilitado={estado.tipo !== "datos"}>
            {t.tareas.apuntar}
          </Boton>
          <Boton onClick={() => setAlta("tarea")} deshabilitado={estado.tipo !== "datos"}>
            <Icono nombre="mas" />
            {t.tareas.nueva}
          </Boton>
        </div>
      </div>

      {alta !== "nada" ? (
        <FormularioActividad
          key={alta}
          apuntar={alta === "apuntar"}
          onGuardada={(a) => {
            setAlta("nada");
            if ((a.status === "DONE") !== (vista === "DONE")) setVista(a.status === "DONE" ? "DONE" : "PENDING");
            else void cargar();
          }}
          onCancelar={() => setAlta("nada")}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}

      {estado.tipo === "cargando" ? <p>{t.tareas.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.tareas.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.tareas.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.tareas.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "datos" && vista === "PENDING" && estado.lista.length === 0 && alta === "nada" ? (
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="tareas" />
          </span>
          <p className="estado-vacio-titulo">{t.tareas.vacioTitulo}</p>
          <p>{t.tareas.vacio}</p>
        </div>
      ) : null}
      {recienHecha !== null && vista === "PENDING" ? (
        <div className="actividades-propuesta">
          <Aviso tono="exito">
            {t.tareas.recienHecha(recienHecha.title)}
            {siguiente?.dueDate != null ? ` ${t.tareas.siguiente(diaLegible(siguiente.dueDate))}` : ""}
          </Aviso>
          <Boton variante="terciario" onClick={() => void deshacer(recienHecha)}>
            {t.tareas.deshacerRecienHecha}
          </Boton>
        </div>
      ) : null}
      {estado.tipo === "datos" && vista === "PENDING" ? (
        <Agrupadas lista={estado.lista} enFicha={false} onCambio={alCambiar} onSesionCaducada={onSesionCaducada} />
      ) : null}

      {estado.tipo === "datos" && vista === "DONE" && estado.lista.length === 0 ? <Aviso tono="info">{t.tareas.vacioHechas}</Aviso> : null}
      {estado.tipo === "datos" && vista === "DONE" && estado.lista.length > 0 ? (
        <>
          <ul className="actividades">
            {estado.lista.map((a) => (
              <FilaActividad key={a.id} a={a} enFicha={false} onCambio={() => void cargar()} onSesionCaducada={onSesionCaducada} />
            ))}
          </ul>
          {estado.cortada ? <p className="ayuda-neutra">{t.tareas.cortada}</p> : null}
        </>
      ) : null}
    </Tarjeta>
    </EnlacesContext.Provider>
  );
}

// ------------------------------------------------------------ en una ficha

/**
 * En la ficha de un cliente, una oportunidad, un presupuesto o una factura: lo
 * pendiente y su historial completo. Fuera de Business no se pinta nada: la
 * ficha ya es del cliente o del documento.
 *
 * `seguimiento` añade el botón que deja el formulario escrito (E-04), y
 * `cobrada` hace que la ficha de una factura PROPONGA cerrar lo pendiente
 * cuando acaba de quedar cobrada. Lo propone: no lo cierra sola.
 *
 * `cerrada` hace lo mismo con una oportunidad recién ganada o perdida, pero
 * con la lista a la vista y todas marcadas: se desmarca la que siga haciendo
 * falta (lo eligió el propietario el 2026-10-25, tras probarlo en staging).
 */
export function BloqueActividades({
  clienteId = null,
  oportunidadId = null,
  presupuestoId = null,
  facturaId = null,
  seguimiento = null,
  cobrada = false,
  cerrada = null,
  onAbrirPresupuesto,
  onAbrirFactura,
  onSesionCaducada,
}: {
  clienteId?: string | null;
  oportunidadId?: string | null;
  presupuestoId?: string | null;
  facturaId?: string | null;
  seguimiento?: { readonly etiqueta: string; readonly plantilla: Plantilla } | null;
  cobrada?: boolean;
  cerrada?: "WON" | "LOST" | null;
  onAbrirPresupuesto?: (id: string) => void;
  onAbrirFactura?: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  const [alta, setAlta] = useState<"nada" | "tarea" | "apuntar" | "seguimiento">("nada");
  const [propuestaVista, setPropuestaVista] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  // Las que el usuario desmarca de la propuesta de `cerrada`: esas se quedan.
  const [seQuedan, setSeQuedan] = useState<ReadonlySet<string>>(new Set());
  const filtro = useMemo(() => ({ clienteId, oportunidadId, presupuestoId, facturaId }), [clienteId, oportunidadId, presupuestoId, facturaId]);
  const enlaces = useMemo(
    () => ({ abrirPresupuesto: onAbrirPresupuesto, abrirFactura: onAbrirFactura, ficha: filtro }),
    [onAbrirPresupuesto, onAbrirFactura, filtro],
  );
  const pendientes = usarActividades("PENDING", filtro, onSesionCaducada);
  const hechas = usarActividades("DONE", filtro, onSesionCaducada);
  const recargar = (): void => {
    void pendientes.cargar();
    void hechas.cargar();
  };
  const p = pendientes.estado;
  const h = hechas.estado;
  if (p.tipo !== "datos" || h.tipo !== "datos") return null;

  async function cerrarPendientes(lista: readonly Actividad[]): Promise<void> {
    setCerrando(true);
    for (const a of lista) {
      const r = await api.marcarActividad(a.id, true);
      if (!r.ok && r.estado === 401) return onSesionCaducada();
    }
    setCerrando(false);
    setPropuestaVista(true);
    recargar();
  }

  return (
    <EnlacesContext.Provider value={enlaces}>
      <Tarjeta titulo={t.tareas.enFicha}>
        {cobrada && !propuestaVista && p.lista.length > 0 ? (
          <div className="actividades-propuesta">
            <Aviso tono="exito">{t.tareas.cobrada(p.lista.length)}</Aviso>
            <div className="fila-botones">
              <Boton onClick={() => void cerrarPendientes(p.lista)} deshabilitado={cerrando}>
                {t.tareas.cerrarPendientes(p.lista.length)}
              </Boton>
              <Boton variante="terciario" onClick={() => setPropuestaVista(true)}>
                {t.tareas.dejarPendientes(p.lista.length)}
              </Boton>
            </div>
          </div>
        ) : null}
        {cerrada !== null && !cobrada && !propuestaVista && p.lista.length > 0 ? (
          <div className="actividades-propuesta">
            <Aviso tono="info">{t.tareas.oportunidadCerrada(cerrada, p.lista.length)}</Aviso>
            <ul className="actividades-propuesta-lista">
              {p.lista.map((a) => (
                <li key={a.id}>
                  <Casilla
                    id={`terminar-${a.id}`}
                    etiqueta={a.title}
                    marcada={!seQuedan.has(a.id)}
                    onChange={(v) =>
                      setSeQuedan((s) => {
                        const n = new Set(s);
                        if (v) n.delete(a.id);
                        else n.add(a.id);
                        return n;
                      })
                    }
                  />
                </li>
              ))}
            </ul>
            <div className="fila-botones">
              <Boton
                onClick={() => void cerrarPendientes(p.lista.filter((a) => !seQuedan.has(a.id)))}
                deshabilitado={cerrando || p.lista.every((a) => seQuedan.has(a.id))}
              >
                {t.tareas.darPorTerminadas}
              </Boton>
              <Boton variante="terciario" onClick={() => setPropuestaVista(true)}>
                {t.tareas.dejarPendientes(p.lista.length)}
              </Boton>
            </div>
          </div>
        ) : null}
        <div className="fila-botones">
          {seguimiento !== null ? (
            <Boton variante="secundario" onClick={() => setAlta("seguimiento")}>
              {seguimiento.etiqueta}
            </Boton>
          ) : null}
          <Boton variante="secundario" onClick={() => setAlta("tarea")}>
            {t.tareas.nueva}
          </Boton>
          <Boton variante="secundario" onClick={() => setAlta("apuntar")}>
            {t.tareas.apuntar}
          </Boton>
        </div>
        {alta !== "nada" ? (
          <FormularioActividad
            key={alta}
            apuntar={alta === "apuntar"}
            plantilla={alta === "seguimiento" ? (seguimiento?.plantilla ?? null) : null}
            clienteFijo={clienteId}
            oportunidadFija={oportunidadId}
            presupuestoFijo={presupuestoId}
            facturaFija={facturaId}
            onGuardada={() => {
              setAlta("nada");
              recargar();
            }}
            onCancelar={() => setAlta("nada")}
            onSesionCaducada={onSesionCaducada}
          />
        ) : null}
        {p.lista.length === 0 && h.lista.length === 0 && alta === "nada" ? <p className="ayuda-neutra">{t.tareas.enFichaVacio}</p> : null}
        <Agrupadas lista={p.lista} enFicha onCambio={recargar} onSesionCaducada={onSesionCaducada} />
        {h.lista.length > 0 ? (
          <section className="actividades-grupo" aria-label={t.tareas.historial}>
            <h3 className="actividades-grupo-titulo">{t.tareas.historial}</h3>
            <ul className="actividades">
              {h.lista.map((a) => (
                <FilaActividad key={a.id} a={a} enFicha onCambio={recargar} onSesionCaducada={onSesionCaducada} />
              ))}
            </ul>
          </section>
        ) : null}
      </Tarjeta>
    </EnlacesContext.Provider>
  );
}

/** Los dos botones de seguimiento (E-04): la plantilla se escribe aquí y no en cada ficha. */
export function seguimientoDePresupuesto(numero: string): { readonly etiqueta: string; readonly plantilla: Plantilla } {
  return { etiqueta: t.tareas.seguimiento, plantilla: { kind: "CALL", title: t.tareas.seguimientoPresupuestoTitulo(numero), dueDate: enDias(3) } };
}

export function seguimientoDeCobro(numero: string): { readonly etiqueta: string; readonly plantilla: Plantilla } {
  return { etiqueta: t.tareas.seguimientoCobro, plantilla: { kind: "CALL", title: t.tareas.seguimientoCobroTitulo(numero), dueDate: hoyLocal() } };
}
